import { useState, useEffect } from 'react';
import { analyticsAPI, subscriptionsAPI } from '../services/api';
import { HiChartBar, HiTrendingUp, HiCurrencyDollar, HiDownload } from 'react-icons/hi';
import GraficaEvolucion from '../components/GraficaEvolucion';
import RentabilidadEsperada from '../components/RentabilidadEsperada';
import { useTranslation } from 'react-i18next';
import { useCarga } from '../hooks/useCarga';
import ErrorAlCargar from '../components/ErrorAlCargar';
import MenuDesplegable from '../components/MenuDesplegable';
import { mensajeDeError } from '../utils/apiError';
import { notify } from '../utils/notify';


/**
 * Los nombres de las acciones que consumen cuota.
 *
 * Escrito a mano y no `t('subscription.action_' + accion)`, por lo mismo que en
 * la pantalla de Suscripción: una clave construida al vuelo no la puede
 * comprobar `npm run i18n:check`, y cuando falta **se imprime tal cual en la
 * pantalla**. Ya pasó: «Subscription.Action_search» en mitad de la página.
 */
const ETIQUETA_DE_ACCION = {
    search: 'subscription.action_product_search',
    import: 'subscription.action_product_import',
    ai_analysis: 'subscription.action_ai_analysis'
};

export default function Analytics() {
    const [data, setData] = useState(null);
    const { cargando: loading, error, ejecutar, reintentar } = useCarga();
    const [period, setPeriod] = useState('30d');
    const { t } = useTranslation();


    useEffect(() => {
        fetchAnalytics();
    }, [period]);

    const fetchAnalytics = () =>
        ejecutar(async () => {
            // `getSales` y `getProducts` llevaban meses construidos —en el
            // servidor y en el cliente— sin que ninguna pantalla los llamara.
            // Son la evolución y el ranking que hacían falta para que Métricas
            // dejara de ser el Panel repetido.
            const [dashboard, roi, ventas, top, rentabilidad, suscripcion] = await Promise.all([
                analyticsAPI.getDashboard(period),
                analyticsAPI.getROI(),
                analyticsAPI.getSales({ period }),
                analyticsAPI.getProducts(5),
                analyticsAPI.getRentabilidad(),
                subscriptionsAPI.getStatus()
            ]);
            setData({
                ...dashboard.data.data,
                roi: roi.data.data,
                serie: ventas.data.data.chartData || [],
                top: top.data.data.products || [],
                rentabilidad: rentabilidad.data.data,
                uso: suscripcion.data.data.usage
            });
        });

    const handleExport = async (type) => {
        try {
            const response = await analyticsAPI.export(type, 'csv');
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${type}-export-${Date.now()}.csv`;
            a.click();
        } catch (error) {
            notify.error(mensajeDeError(error, t('analytics.export_failed')));
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-1/4 animate-pulse"></div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[...Array(3)].map((_, i) => (
                        <div key={i} className="h-32 bg-slate-200 dark:bg-slate-700 rounded-xl animate-pulse"></div>
                    ))}
                </div>
                <div className="h-80 bg-slate-200 dark:bg-slate-700 rounded-xl animate-pulse"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="space-y-8">
                <h1 className="page-title">{t('analytics.title')}</h1>
                <ErrorAlCargar error={error} onReintentar={reintentar} />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="page-title flex items-center gap-2">
                        <HiChartBar className="w-8 h-8 text-purple-500" />
                        {t('analytics.title')}
                    </h1>
                    <p className="text-slate-500 dark:text-slate-400 mt-1">
                        {t('analytics.subtitle')}
                    </p>
                </div>

                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        {['7d', '30d', '90d'].map((p) => (
                            <button
                                key={p}
                                onClick={() => setPeriod(p)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${period === p
                                        ? 'bg-primary-500 text-white'
                                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                                    }`}
                            >
                                {p === '7d' ? t('analytics.period_7d') : p === '30d' ? t('analytics.period_30d') : t('analytics.period_90d')}
                            </button>
                        ))}
                    </div>

                    {/* Se abría solo al pasar el ratón: en una tableta no
                        había forma de exportar. */}
                    <MenuDesplegable
                        etiqueta={t('analytics.export')}
                        anchura="w-44"
                        claseBoton="btn-secondary"
                        boton={
                            <>
                                <HiDownload className="w-5 h-5 mr-2" />
                                {t('analytics.export')}
                            </>
                        }
                    >
                        {(cerrar) => (
                            <>
                                <button
                                    type="button"
                                    role="menuitem"
                                    onClick={() => { cerrar(); handleExport('orders'); }}
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-700"
                                >
                                    {t('analytics.export_orders')}
                                </button>
                                <button
                                    type="button"
                                    role="menuitem"
                                    onClick={() => { cerrar(); handleExport('products'); }}
                                    className="w-full text-left px-4 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-700"
                                >
                                    {t('analytics.export_products')}
                                </button>
                            </>
                        )}
                    </MenuDesplegable>
                </div>
            </div>

            {/* Lo que se espera ganar. Va lo primero porque es lo único que
                dice algo antes de la primera venta, y eso es hoy. */}
            <RentabilidadEsperada datos={data?.rentabilidad} />

            {/* La evolución en el tiempo. */}
            <div className="card">
                <h3 className="section-title mb-6">{t('analytics.evolution_title')}</h3>
                {data?.overview?.totalOrders > 0 ? (
                    <GraficaEvolucion datos={data.serie} />
                ) : (
                    /* Sin ventas no se dibuja una línea plana en cero: parece un
                       negocio que va mal, y lo que pasa es que no ha empezado. */
                    <p className="text-sm text-slate-500">{t('analytics.evolution_empty')}</p>
                )}
            </div>

            {/* Qué productos rinden. */}
            <div className="card">
                <h3 className="section-title mb-6">{t('analytics.top_title')}</h3>
                {data?.top?.length > 0 ? (
                    <ul className="space-y-3">
                        {data.top.map((p) => {
                            const pedidos = p.orders?.[0]?.count ?? 0;
                            const mayor = Math.max(1, ...data.top.map((o) => o.orders?.[0]?.count ?? 0));
                            return (
                                <li key={p.id} className="flex items-center gap-3">
                                    <img
                                        src={p.main_image}
                                        alt=""
                                        className="w-10 h-10 rounded object-cover bg-slate-100 dark:bg-slate-700 shrink-0"
                                    />
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-sm text-slate-900 dark:text-white truncate">
                                            {p.custom_title}
                                        </span>
                                        <span className="block h-2 mt-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                            <span
                                                className="block h-full bg-primary-500 rounded-full"
                                                style={{ width: `${(pedidos / mayor) * 100}%` }}
                                            />
                                        </span>
                                    </span>
                                    <span className="text-sm font-medium text-slate-900 dark:text-white shrink-0">
                                        {t('analytics.orders_n', { count: pedidos })}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <p className="text-sm text-slate-500">{t('analytics.top_empty')}</p>
                )}
            </div>

            {/* Cuánto de la cuota diaria llevas gastado. Responde a una decisión
                concreta: si te quedas corto a menudo, el plan se te queda
                pequeño. Se calculaba ya y solo se veía en Suscripción. */}
            {data?.uso && (
                <div className="card">
                    <h3 className="section-title mb-6">{t('analytics.quota_title')}</h3>
                    <ul className="space-y-3">
                        {Object.entries(data.uso).map(([accion, u]) => {
                            const ilimitado = u.limit === 'unlimited';
                            const proporcion = ilimitado ? 0 : Math.min(1, u.used / u.limit);
                            return (
                                <li key={accion} className="flex items-center gap-3 text-sm">
                                    <span className="w-40 shrink-0 text-slate-600 dark:text-slate-300">
                                        {ETIQUETA_DE_ACCION[accion] ? t(ETIQUETA_DE_ACCION[accion]) : accion}
                                    </span>
                                    <span className="flex-1 h-2.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                        {!ilimitado && (
                                            <span
                                                className={`block h-full rounded-full ${proporcion >= 1 ? 'bg-danger-500' : proporcion >= 0.8 ? 'bg-warning-500' : 'bg-primary-500'}`}
                                                style={{ width: `${proporcion * 100}%` }}
                                            />
                                        )}
                                    </span>
                                    {/* El infinito, igual que en Suscripción: es
                                        el mismo símbolo en los dos idiomas. */}
                                    <span className="w-24 text-right text-slate-500">
                                        {u.used} / {ilimitado ? '∞' : u.limit}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            {/* ROI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="card">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                            <HiCurrencyDollar className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <p className="text-sm text-slate-500">{t('analytics.total_revenue')}</p>
                            <p className="text-2xl font-bold text-slate-900 dark:text-white">
                                ${parseFloat(data?.roi?.totalRevenue || 0).toFixed(2)}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                            <HiTrendingUp className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <p className="text-sm text-slate-500">{t('analytics.total_profit')}</p>
                            <p className="text-2xl font-bold text-green-500">
                                ${parseFloat(data?.roi?.totalProfit || 0).toFixed(2)}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                            <HiChartBar className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <p className="text-sm text-slate-500">{t('analytics.roi')}</p>
                            <p className="text-2xl font-bold text-slate-900 dark:text-white">
                                {parseFloat(data?.roi?.roi || 0).toFixed(1)}%
                            </p>
                            {/* No es «tu ROI»: `/analytics/roi` solo suma los
                                pedidos entregados. Sin decirlo, un pedido en
                                camino parece no existir. */}
                            <p className="text-xs text-slate-400 mt-0.5">{t('analytics.roi_delivered_only')}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Overview Stats */}
            <div className="card">
                <h3 className="section-title mb-6">{t('analytics.performance_overview')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                        <p className="text-3xl font-bold text-slate-900 dark:text-white">
                            {data?.overview?.totalOrders || 0}
                        </p>
                        <p className="text-sm text-slate-500 mt-1">{t('analytics.total_orders')}</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                        <p className="text-3xl font-bold text-green-500">
                            {data?.overview?.completedOrders || 0}
                        </p>
                        <p className="text-sm text-slate-500 mt-1">{t('analytics.completed')}</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                        <p className="text-3xl font-bold text-yellow-500">
                            {data?.overview?.pendingOrders || 0}
                        </p>
                        <p className="text-sm text-slate-500 mt-1">{t('analytics.pending')}</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                        <p className="text-3xl font-bold text-slate-900 dark:text-white">
                            ${parseFloat(data?.overview?.averageOrderValue || 0).toFixed(2)}
                        </p>
                        <p className="text-sm text-slate-500 mt-1">{t('analytics.avg_order_value')}</p>
                    </div>
                </div>
            </div>

            {/* Profit Margin */}
            <div className="card">
                <h3 className="section-title mb-6">{t('analytics.profit_margin_analysis')}</h3>
                <div className="space-y-4">
                    <div>
                        <div className="flex justify-between text-sm mb-2">
                            <span className="text-slate-600 dark:text-slate-400">{t('analytics.profit_margin')}</span>
                            <span className="font-medium text-slate-900 dark:text-white">
                                {parseFloat(data?.roi?.profitMargin || 0).toFixed(1)}%
                            </span>
                        </div>
                        <div className="h-3 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-500"
                                style={{ width: `${Math.min(data?.roi?.profitMargin || 0, 100)}%` }}
                            ></div>
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4 pt-4">
                        <div className="text-center">
                            <p className="text-sm text-slate-500">{t('analytics.revenue')}</p>
                            <p className="text-lg font-semibold text-slate-900 dark:text-white">
                                ${parseFloat(data?.roi?.totalRevenue || 0).toFixed(0)}
                            </p>
                        </div>
                        <div className="text-center">
                            <p className="text-sm text-slate-500">{t('analytics.costs')}</p>
                            <p className="text-lg font-semibold text-red-500">
                                ${parseFloat(data?.roi?.totalCost || 0).toFixed(0)}
                            </p>
                        </div>
                        <div className="text-center">
                            <p className="text-sm text-slate-500">{t('analytics.net_profit')}</p>
                            <p className="text-lg font-semibold text-green-500">
                                ${parseFloat(data?.roi?.totalProfit || 0).toFixed(0)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
